import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { generateKeyBetween } from 'fractional-indexing';
import type { Product as ProductDTO } from '@setvi/shared/interfaces';
import type {
  QuoteItemDTO,
  QuoteProductDTO
} from '@setvi/shared/services/react-query';
import { addQuoteV2ProductsMutation } from 'Services/Mutations/QuotesV2';
import type {
  AddQuoteV2ProductsResponse,
  QuoteV2Item
} from 'Services/Query/QuotesV2/Types';
import { getLastPositionInContainer } from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

interface AddProductsVariables {
  products: ProductDTO[];
  parentId?: string | null;
}

/** Resolve initial quantity respecting minimumQuoteQuantity */
const getInitialQuantity = (product: ProductDTO): number => {
  if (
    product.minimumQuoteQuantity &&
    product.minimumQuoteQuantity > 1 &&
    product.overrideMinimumQuoteQuantity !== true
  ) {
    return product.minimumQuoteQuantity;
  }
  return product.quantity ?? 1;
};

/** Build an optimistic QuoteProductDTO from a catalog ProductDTO */
const buildOptimisticProduct = (
  product: ProductDTO,
  tempId: string,
  sectionId: string
): QuoteProductDTO => {
  const sell = product.sell ?? product.price ?? 0;
  const cost = product.cost ?? 0;

  return {
    _id: tempId,
    id: tempId,
    type: 'product',
    productId: product._id,
    name: product.name,
    sku: product.sku || '',
    quantity: getInitialQuantity(product),
    sell,
    cost,
    note: null,
    vendor: product.vendor
      ? { name: (product.vendor as any).Name || '', _id: null, vendorId: null }
      : null,
    images: product.images as any,
    imageUrl: product.imageUrl || '',
    isFreeHand: false,
    isEditable: product.isEditable ?? true,
    labelType: '',
    customLabel: '',
    includeInTotalCost: false,
    minimumQuoteQuantity: product.minimumQuoteQuantity ?? 1,
    overrideMinimumQuoteQuantity: product.overrideMinimumQuoteQuantity ?? false,
    children: [],
    parentID: sectionId || null,
    totalGrossProfit: sell - cost,
    totalGrossProfitPercentage: 0,
    description: product.description || '',
    packSize: product.packSize || '',
    unitOfMeasure: product.unitOfMeasure || '',
    manufacturerSku: product.manufacturerSku || '',
    isPricingFetching: true
  } as unknown as QuoteProductDTO;
};

/** Transform a server QuoteV2Item to QuoteProductDTO using server-computed margin values */
const transformResponseItem = (item: QuoteV2Item): QuoteProductDTO => {
  const p = item.product;
  return {
    _id: item._id,
    id: item._id,
    type: 'product',
    productId: p.productId || '',
    name: p.name || '',
    sku: p.sku || '',
    quantity: p.quantity || 1,
    sell: p.sell || 0,
    cost: p.cost || 0,
    note: p.note || null,
    vendor: p.vendor || null,
    images: p.images || null,
    imageUrl: p.images?.[0]?.path || '',
    isFreeHand: p.isFreeHand || false,
    isEditable: p.isEditable || false,
    labelType: p.labelType || '',
    customLabel: p.customLabel || '',
    includeInTotalCost: p.includeInTotalCost || false,
    minimumQuoteQuantity: p.minimumQuoteQuantity ?? 1,
    overrideMinimumQuoteQuantity: p.overrideMinimumQuoteQuantity ?? false,
    children: [],
    parentID: item.parentId || null,
    totalGrossProfit: p.marginAmount,
    totalGrossProfitPercentage: p.margin,
    description: p.description || '',
    packSize: p.packSize || '',
    unitOfMeasure: p.unitOfMeasure || '',
    manufacturerSku: p.manufacturerSku || ''
  } as unknown as QuoteProductDTO;
};

/** Insert products optimistically into items list (at root or inside a section) */
const addItemsOptimistically = (
  items: QuoteItemDTO[],
  newItems: QuoteProductDTO[],
  sectionId: string
): QuoteItemDTO[] => {
  if (!sectionId) return [...items, ...newItems];

  return items.map(item => {
    if (
      item.type === 'section' &&
      (item.id || (item as any)._id) === sectionId
    ) {
      return {
        ...item,
        children: [...((item as any).children || []), ...newItems]
      };
    }
    return item;
  });
};

/** Remove items by their IDs from the nested items list */
const removeItemsByIds = (
  items: QuoteItemDTO[],
  ids: Set<string>,
  sectionId: string
): QuoteItemDTO[] => {
  if (!sectionId) {
    return items.filter(item => !ids.has(item.id || (item as any)._id));
  }
  return items.map(item => {
    if (
      item.type === 'section' &&
      (item.id || (item as any)._id) === sectionId
    ) {
      return {
        ...item,
        children: ((item as any).children || []).filter(
          (child: any) => !ids.has(child.id || child._id)
        )
      };
    }
    return item;
  });
};

/**
 * Add catalog products to a quote via the dedicated v2 products endpoint.
 * Applies an optimistic update immediately, then replaces optimistic items
 * with real server data (including server-computed marginAmount) on success.
 */
export const useAddProducts = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = addQuoteV2ProductsMutation();

  return useMutation({
    mutationFn: async ({ products, parentId }: AddProductsVariables) => {
      const sectionId = parentId || '';
      const items = cache.getItems() || [];

      // Generate sequential fractional positions scoped to the target container
      let lastPosition = getLastPositionInContainer(items, sectionId || null);

      const productInputs = products.map(product => {
        const position = generateKeyBetween(lastPosition, null);
        lastPosition = position;
        const sell = product.sell ?? product.price;
        return {
          productId: product._id,
          cost: product.cost ?? 0,
          ...(sell != null ? { sell } : {}),
          quantity: getInitialQuantity(product),
          sectionId,
          position
        };
      });

      return mutationFn({ quoteId, products: productInputs });
    },

    onMutate: async ({ products, parentId }) => {
      await cache.cancel();

      const previousItems = cache.getItems();
      const sectionId = parentId || '';
      const tempIds = products.map(() => uuidv4());

      // Compute positions from pre-optimistic data, scoped to target container
      let lastPos = getLastPositionInContainer(
        previousItems || [],
        sectionId || null
      );
      const newItems = products.map((p, i) => {
        const position = generateKeyBetween(lastPos, null);
        lastPos = position;
        return {
          ...buildOptimisticProduct(p, tempIds[i], sectionId),
          position
        } as unknown as QuoteProductDTO;
      });

      cache.setItems(old =>
        addItemsOptimistically(old || [], newItems, sectionId)
      );

      return { previousItems, tempIds };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: (data, variables, context) => {
      const response = data as AddQuoteV2ProductsResponse;
      const sectionId = variables.parentId || '';

      if (response?.items?.length && context?.tempIds?.length) {
        // Replace optimistic items with real server items (which include marginAmount)
        const tempIdSet = new Set(context.tempIds);
        const realItems = response.items
          .filter(item => item.type === 'product')
          .map(transformResponseItem);

        cache.setItems(old => {
          if (!old) return old;
          const withoutOptimistic = removeItemsByIds(old, tempIdSet, sectionId);
          return addItemsOptimistically(
            withoutOptimistic,
            realItems,
            sectionId
          );
        });
      }

      cache.invalidate();
    }
  });
};
